import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useState } from "react";
import { Badge, Button, Table } from "react-bootstrap";
import {
  getQuoteSignoff,
  setQuotePmToCheck,
  setQuoteSignoff,
  listUsers,
  getTypedNotCheckedReasons,
  setQuoteNotCheckedReason,
} from "../../services/apiClient.js";
import { formatDateTime } from "../../utils/date.js";
import { rowLabel, selectableRows } from "../../utils/people.js";

// Quote sign-off: mark the quote Typed, then Checked. For Standard R&Q jobs the
// status engine blocks Quoted/Accepted until both are done (the rule lives in
// services/claimStatus.js, so it holds for the inspector gateway too).
//
// "To be checked by" (1.0 QuotePage "Assign PM To Check", Karen 2026-07-19):
// once Typed, pick WHO should check — advisory routing that decides whose
// column the quote sits in on the Typed Not Checked worklist; it never limits
// who may tick Checked (1.0 behaviour). Unlike 1.0 (where the dropdown only
// existed in the postback right after typing), it stays editable the whole
// typed-not-checked window.

function QuoteSignoff({ claimId, onChanged }) {
  const [s, setS] = useState(null);
  const [users, setUsers] = useState([]);
  const [reasons, setReasons] = useState([]); // the "Waiting on" catalogue
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  function load() {
    getQuoteSignoff(claimId)
      .then(setS)
      .catch(() => setError("Unable to load sign-off."));
  }
  useEffect(() => {
    load();
    // The checker list = the PM population (1.0 LoadProjectManagersList).
    listUsers({ pco: true }).then(setUsers).catch(() => {});
    getTypedNotCheckedReasons().then(setReasons).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  async function toggle(which, value) {
    setError("");
    setSaving(true);
    try {
      setS(await setQuoteSignoff(claimId, which, value));
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to update sign-off.");
    } finally {
      setSaving(false);
    }
  }

  async function assignChecker(userId) {
    setError("");
    try {
      setS(await setQuotePmToCheck(claimId, userId ? Number(userId) : null));
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to assign the checker.");
    }
  }

  async function setWaitingOn(reasonId) {
    if (reasonId === "__current") return; // the retired phrase already on the job
    setError("");
    try {
      setS(await setQuoteNotCheckedReason(claimId, reasonId ? Number(reasonId) : null));
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to set the waiting-on reason.");
    }
  }

  // Checked waits for the PC Sums tick (KS 2026-09-08), every quote.
  const pcSumsBlock = !!s?.typed_at && !s?.pc_sums_confirmed_at;

  function markButton(label, which, repeat) {
    const blocked = which === "checked" && (!s?.typed_at || pcSumsBlock);
    return (
      <span
        className="ms-auto d-inline-block"
        data-hint={
          which === "checked" && !s?.typed_at ? "Must mark the quote Typed above before you can mark it as checked"
          : which === "checked" && pcSumsBlock ? "Must check PC Sums above before you can mark quote as checked"
          : repeat && which === "typed" ? "Records a new typing and requires a fresh check and PC Sum confirmation; earlier sign-offs stay in history"
          : undefined
        }
      >
        <Button size="sm" variant="success" disabled={saving || blocked}
          style={blocked ? { pointerEvents: "none" } : undefined}
          onClick={() => toggle(which, true)}>
          {label === "PC Sums" ? "PC Sum cost types entered"
            : repeat ? `${which === "typed" ? "Retyped" : "Rechecked"} by me`
            : `Mark ${label} by me`}
        </Button>
      </span>
    );
  }

  function row(label, at, byName, which) {
    const done = !!at;
    const repeat = s.history?.some((h) => h.kind === which);
    return (
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <span style={{ minWidth: 70 }} className="fw-semibold">{label}</span>
        {done ? (
          <>
            <Badge bg="success">Done</Badge>
            <span className="text-muted small">
              by {byName || "—"} · {formatDateTime(at)}
            </span>
            {which !== "pc-sums" ? markButton(label, which, true) : null}
            <Button size="sm" variant="danger" className={which === "pc-sums" ? "ms-auto" : undefined}
              disabled={saving} onClick={() => toggle(which, false)}>
              Clear
            </Button>
          </>
        ) : (
          <>
            <Badge bg="light" text="dark">{label === "PC Sums" ? "Cost types not confirmed" : `Not ${label.toLowerCase()}`}</Badge>
            {markButton(label, which, repeat)}
          </>
        )}
      </div>
    );
  }

  // Between Typed and Checked the assignment is live routing (editable);
  // after Checked it's history (shown read-only when set).
  function checkerRow() {
    if (!s?.typed_at) return null;
    if (s.checked_at) {
      return s.pm_to_check_id ? (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <span style={{ minWidth: 70 }} className="fw-semibold">Checker</span>
          <span className="text-muted small">was assigned to {s.pm_to_check_name}</span>
        </div>
      ) : null;
    }
    return (
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <span style={{ minWidth: 70 }} className="fw-semibold">Check by</span>
        <ThemedSelect
          size="sm"
          style={{ maxWidth: 220 }}
          value={s.pm_to_check_id || ""}
          onChange={(e) => assignChecker(e.target.value)}
          data-hint="Whose worklist this quote-to-check appears on — anyone may still tick Checked"
        >
          <option value="">— Not assigned —</option>
          {/* Retired accounts aren't offered, but the one this quote is already
              routed to stays listed — see utils/people.js. */}
          {selectableRows(users, s.pm_to_check_id).map((u) => (
            <option key={u.id} value={u.id}>{rowLabel(u)}</option>
          ))}
        </ThemedSelect>
        <span className="text-muted small">routes it to their Typed Not Checked list</span>
      </div>
    );
  }

  // "Waiting on" (1.0's dropdown beside the red QUOTE NOT CHECKED label): WHY
  // this typed quote is still to be checked — Contractor to Quote, Insurance Co
  // to Advise… KR's shorthand for working her Typed Not Checked list, back on
  // her asking (KS via Robert, 2026-09-17). Only in the typed-not-checked
  // window: ticking Checked clears it, as 1.0 did, and the RDR's Typed Not
  // Checked summary groups by it. A phrase since retired from the catalogue
  // stays readable on the job rather than showing as "no reason".
  function waitingRow() {
    if (!s?.typed_at || s.checked_at) return null;
    const current = reasons.find((r) => r.reason === s.not_checked_reason);
    const value = current ? current.id : s.not_checked_reason ? "__current" : "";
    return (
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <span style={{ minWidth: 70 }} className="fw-semibold">Waiting on</span>
        <ThemedSelect
          size="sm"
          style={{ maxWidth: 220 }}
          value={value}
          onChange={(e) => setWaitingOn(e.target.value)}
          data-hint="Why this typed quote is still to be checked — clears itself when it is marked Checked"
        >
          <option value="">— No reason given —</option>
          {reasons.map((r) => (
            <option key={r.id} value={r.id}>{r.reason}</option>
          ))}
          {value === "__current" ? <option value="__current">{s.not_checked_reason}</option> : null}
        </ThemedSelect>
        <span className="text-muted small">why it is still to be checked</span>
      </div>
    );
  }

  return (
    <div>
      <h5 className="mb-1">Quote Sign-off</h5>
      <p className="text-muted small mb-3">
        Standard R&amp;Q jobs must have the quote <strong>Typed</strong> and{" "}
        <strong>Checked</strong> before they can be Quoted or Accepted.
      </p>

      <p className="text-muted small mb-3">
        Retyping requires a fresh check and PC Sum confirmation. Earlier sign-offs stay in history.
      </p>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      {s ? (
        <>
          {row("Typed", s.typed_at, s.typed_by_name, "typed")}
          {/* KS 2026-08-25: the typist attests PC Sum COST TYPES are entered
              on every job; since 2026-09-08 Checked waits for it (pcSumsBlock).
              The wording badge catches labelled misses; this tick is for the
              unlabelled ones. (The pre-tick "not tracked" display went with the
              gate: an old quote still to be checked needs the tick to press.) */}
          {row("PC Sums", s.pc_sums_confirmed_at, s.pc_sums_confirmed_by_name, "pc-sums")}
          {checkerRow()}
          {waitingRow()}
          {row("Checked", s.checked_at, s.checked_by_name, "checked")}
          {s.history?.length ? (
            <details className="mt-3">
              <summary className="small">Sign-off history ({s.history.length})</summary>
              <p className="text-muted small my-2">All recorded marks, including those since cleared. Current sign-off is shown above.</p>
              <Table striped bordered hover responsive size="sm" className="mb-0">
                <thead className="table-surface">
                  <tr><th>Sign-off</th><th>By</th><th>When</th></tr>
                </thead>
                <tbody>
                  {[...s.history].reverse().map((h) => (
                    <tr key={h.id}>
                      <td>{h.kind === "typed" ? "Typed" : "Checked"}</td>
                      <td>{h.signed_by_name || "—"}</td>
                      <td>{formatDateTime(h.signed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </details>
          ) : null}
        </>
      ) : (
        <p className="text-muted mb-0">Loading…</p>
      )}
    </div>
  );
}

export default QuoteSignoff;
